/**
 * Centralized Role Configuration
 * Single source of truth for all user roles in the system
 */

// Role definitions with metadata
export const ROLES = {
  RECRUITER: {
    value: 'recruiter',
    label: 'Recruiter',
    description: 'Standard recruiter role',
    color: 'green',
    colorClasses: 'bg-green-100 text-green-800 border-green-200',
    permissions: ['view_jobs', 'view_candidates', 'create_submissions', 'view_team_data'],
    hierarchy: 1
  },
  LEAD_RECRUITER: {
    value: 'lead_recruiter',
    label: 'Lead Recruiter',
    description: 'Leads a recruiting team',
    color: 'blue',
    colorClasses: 'bg-blue-100 text-blue-800 border-blue-200',
    permissions: ['view_jobs', 'view_candidates', 'create_submissions', 'view_team_data', 'manage_team', 'assign_clients'],
    hierarchy: 2
  },
  RECRUITER_SUPERVISOR: {
    value: 'recruiter_supervisor',
    label: 'Recruiter Supervisor',
    description: 'Supervises multiple recruiting teams',
    color: 'purple',
    colorClasses: 'bg-purple-100 text-purple-800 border-purple-200',
    permissions: ['view_jobs', 'view_candidates', 'create_submissions', 'view_team_data', 'manage_team', 'assign_clients', 'view_all_teams'],
    hierarchy: 3
  },
  ACCOUNT_MANAGER: {
    value: 'account_manager',
    label: 'Account Manager',
    description: 'Manages client relationships and accounts',
    color: 'orange',
    colorClasses: 'bg-orange-100 text-orange-800 border-orange-200',
    permissions: ['view_jobs', 'view_candidates', 'create_submissions', 'view_team_data', 'manage_team', 'assign_clients', 'manage_clients'],
    hierarchy: 2
  },
  ACCOUNT_EXEC: {
    value: 'account_exec',
    label: 'Account Exec',
    description: 'Account Executive role',
    color: 'orange',
    colorClasses: 'bg-orange-50 text-orange-700 border-orange-200',
    permissions: ['view_jobs', 'view_candidates', 'create_submissions', 'view_team_data'],
    hierarchy: 1
  },
  ADMIN: {
    value: 'admin',
    label: 'Administrator',
    description: 'Full system access',
    color: 'red',
    colorClasses: 'bg-red-100 text-red-800 border-red-200',
    permissions: ['*'], // All permissions
    hierarchy: 4
  },
  JUNO_ADMIN: {
    value: 'junoadmin',
    label: 'Juno Admin',
    description: 'Juno platform administrator',
    color: 'red',
    colorClasses: 'bg-red-100 text-red-800 border-red-200',
    permissions: ['*'], // All permissions
    hierarchy: 5
  }
};

// Role groups for filtering and organization
export const ROLE_GROUPS = {
  RECRUITING: {
    label: 'Recruiting',
    roles: [ROLES.RECRUITER, ROLES.LEAD_RECRUITER, ROLES.RECRUITER_SUPERVISOR]
  },
  ACCOUNT_MANAGEMENT: {
    label: 'Account Management',
    roles: [ROLES.ACCOUNT_MANAGER, ROLES.ACCOUNT_EXEC]
  },
  ADMINISTRATION: {
    label: 'Administration',
    roles: [ROLES.ADMIN, ROLES.JUNO_ADMIN]
  }
};

// Team lead eligible roles
export const TEAM_LEAD_ROLES = [
  ROLES.LEAD_RECRUITER.value,
  ROLES.RECRUITER_SUPERVISOR.value,
  ROLES.ACCOUNT_MANAGER.value
];

// Legacy role mappings for backward compatibility
export const LEGACY_ROLE_MAP = {
  'lead': 'lead_recruiter',
  'Lead Recruiter': 'lead_recruiter',
  'Account Manager': 'account_manager',
  'lead_account_manager': 'account_manager',
  'lead_sales_manager': 'account_manager', // Map old sales role
  'sales': 'account_exec', // Map old sales role
  'sales_supervisor': 'recruiter_supervisor'
};

// Get all roles as array
export const getAllRoles = () => Object.values(ROLES);

// Get role by value
export const getRoleByValue = (roleValue) => {
  // Check for legacy mapping first
  const mappedValue = LEGACY_ROLE_MAP[roleValue] || roleValue;
  return getAllRoles().find(role => role.value === mappedValue);
};

// Get role label
export const getRoleLabel = (roleValue) => {
  const role = getRoleByValue(roleValue);
  return role ? role.label : roleValue;
};

// Get role color classes
export const getRoleColorClasses = (roleValue) => {
  const role = getRoleByValue(roleValue);
  return role ? role.colorClasses : 'bg-gray-100 text-gray-800 border-gray-200';
};

// Export for API compatibility
export const getAvailableRolesForAPI = () => {
  return getAllRoles().map(role => ({
    value: role.value,
    label: role.label,
    description: role.description,
    color: role.color
  }));
};
